// File: my-spa/src/components/UserForm.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { User } from './ManageUsers'; // Importa la interfaz
import { UserRole } from '../auth/authContext';
import {
  PERMISSION_GROUPS,
  Permission,
  ROLE_DEFAULT_PERMISSIONS,
  getEffectivePermissions,
} from '../auth/permissions';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';


interface UserFormProps {
  user?: User;
  onSuccess: () => void;
  onCancel: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, onSuccess, onCancel }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.STAFF);
  const [permissions, setPermissions] = useState<string[]>(
    ROLE_DEFAULT_PERMISSIONS[UserRole.STAFF],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!user;
  const isAdmin = role === UserRole.ADMIN;

  useEffect(() => {
    if (user) {
      setUsername(user.username);
      setRole(user.role);
      // Si el usuario no tiene permisos explícitos (null), mostramos los del rol
      setPermissions(getEffectivePermissions(user.role, user.permissions));
    }
    // La contraseña no se pre-puebla por seguridad
  }, [user]);

  const handleRoleChange = (newRole: UserRole) => {
    setRole(newRole);
    // Al cambiar de rol, reiniciamos los accesos a los predeterminados del rol
    setPermissions(ROLE_DEFAULT_PERMISSIONS[newRole] ?? []);
  };

  const togglePermission = (permission: Permission) => {
    setPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((item) => item !== permission)
        : [...prev, permission],
    );
  };

  const isGroupFullySelected = (groupPermissions: Permission[]) =>
    groupPermissions.every((permission) => permissions.includes(permission));

  const toggleGroup = (groupPermissions: Permission[]) => {
    setPermissions((prev) => {
      if (isGroupFullySelected(groupPermissions)) {
        return prev.filter((item) => !groupPermissions.includes(item as Permission));
      }
      return Array.from(new Set([...prev, ...groupPermissions]));
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload: any = {
      username,
      role,
      permissions,
    };

    if (password) {
      payload.password = password;
    }

    // Al crear, la contraseña es obligatoria
    if (!isEditing && !password) {
      toast.error('La contraseña es obligatoria para crear un usuario.');
      setIsSubmitting(false);
      return;
    }

    try {
      if (isEditing) {
        await axios.patch(`${API_BASE_URL}/users/${user!.id}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/users`, payload);
      }
      onSuccess();
    } catch (err: any) {
      console.error("Error saving user:", err);
      toast.error(err.response?.data?.message || 'Error al guardar el usuario.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label" htmlFor="username">Usuario</label>
        <input
          id="username"
          type="text"
          className="form-input"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="password">
          Contraseña {isEditing ? '(dejar en blanco para no cambiar)' : ''}
        </label>
        <input
          id="password"
          type="password"
          className="form-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required={!isEditing} // Requerida solo al crear
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="role">Rol</label>
        <select
          id="role"
          className="form-select"
          value={role}
          onChange={(e) => handleRoleChange(e.target.value as UserRole)}
        >
          <option value={UserRole.ADMIN}>Admin</option>
          <option value={UserRole.STAFF}>Staff</option>
        </select>
      </div>

      {/* --- MENÚ DE ACCESOS POR MÓDULO --- */}
      <div className="form-group">
        <label className="form-label">Accesos por módulo</label>
        {isAdmin ? (
          <p className="permissions-admin-note">
            El rol Administrador tiene acceso a todos los módulos del sistema.
          </p>
        ) : (
          <div className="permissions-panel">
            {PERMISSION_GROUPS.map((group) => {
              const groupPermissions = group.permissions.map((option) => option.key);
              const allSelected = isGroupFullySelected(groupPermissions);
              return (
                <div className="permissions-group" key={group.module}>
                  <label className="permissions-group-header">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => toggleGroup(groupPermissions)}
                    />
                    <span>{group.label}</span>
                  </label>
                  <div className="permissions-group-items">
                    {group.permissions.map((option) => (
                      <label className="permissions-item" key={option.key}>
                        <input
                          type="checkbox"
                          checked={permissions.includes(option.key)}
                          onChange={() => togglePermission(option.key)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button type="button" onClick={onCancel} className="action-button" style={{ backgroundColor: 'var(--color-secondary)' }}>
          Cancelar
        </button>
        <button type="submit" disabled={isSubmitting} className="action-button" style={{ backgroundColor: 'var(--color-success)' }}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
